import {
  useDeleteCustomerAuthMePushSubscriptions,
  useGetPushPublicKey,
  usePostCustomerAuthMePushSubscriptions,
} from '@agos/api-client/customers'
import { tt } from '@agos/shared'
import { useCallback, useEffect, useState } from 'react'

import { hasVapidKey, publicKeyOf, pushState, vapidKeyBytes, type PushState } from './push'

/**
 * Push, from the browser's side — §9.239, FB-397.
 *
 * **One home, because this was already spelled twice and about to be a third time.** The banner held
 * its own copy of the browser probe and its own `enable()`; the profile held a literal `false` and no
 * way to switch anything off. §9.209's lesson is that the copy with a test hides the copy without one,
 * so the probe, the subscribe and the unsubscribe live here and both screens read them.
 *
 * **Three facts decide the state and all three can change under the page**: whether the server has a
 * VAPID key (FB-C03 — it does not, on this stack), what the browser's permission is, and whether this
 * browser holds a subscription. The permission is held in state rather than read once, which fixes a
 * defect found while building this: the banner captured `Notification.permission` at mount, so after a
 * successful subscribe it still read `default`, `mayEnable` stayed true and the banner offered to turn
 * on what had just been turned on.
 */

export interface PushControl {
  state: PushState
  publicKey: string | null
  busy: boolean
  /** What went wrong on the last attempt, in the customer's language. Empty when nothing did. */
  problem: string
  enable: () => Promise<void>
  disable: () => Promise<void>
}

/** The browser's half of the answer. Read on demand, because a permission changes under the page. */
function browserState(): { supported: boolean; permission: NotificationPermission } {
  const supported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  return { supported, permission: supported ? Notification.permission : 'denied' }
}

export function usePush(): PushControl {
  const { data } = useGetPushPublicKey()
  const save = usePostCustomerAuthMePushSubscriptions()
  const drop = useDeleteCustomerAuthMePushSubscriptions()

  const [supported] = useState(() => browserState().supported)
  const [permission, setPermission] = useState<NotificationPermission>(() => browserState().permission)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')

  useEffect(() => {
    if (!supported) return
    // Asking the browser whether it already holds a subscription is asynchronous and cannot be done
    // during render; it is a read, so no cascading-state rule applies.
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => setHasSubscription(subscription !== null))
      .catch(() => setHasSubscription(false))
  }, [supported])

  const publicKey = publicKeyOf(data)
  const state = pushState({ supported, publicKey, permission, hasSubscription })

  /**
   * Ask, subscribe, and tell the server — in that order, and all three.
   *
   * An earlier version stopped after `Notification.requestPermission()`. The browser then said yes and
   * nothing was subscribed to anything: no `pushManager.subscribe`, no row on the server, and a banner
   * that disappeared as though it had worked. Each step can fail in its own way and each says so,
   * because *nothing happened* is the one outcome a customer cannot act on.
   */
  const enable = useCallback(async () => {
    setProblem('')
    if (publicKey === null || !hasVapidKey(publicKey)) {
      setProblem(tt('Server zatiaľ nemá nastavené notifikácie.'))
      return
    }
    setBusy(true)
    try {
      const granted = await Notification.requestPermission()
      setPermission(granted)
      if (granted !== 'granted') {
        setProblem(
          granted === 'denied'
            ? tt('Notifikácie sú zablokované - povoľte ich v nastaveniach prehliadača.')
            : tt('Povolenie zostalo nezodpovedané.'),
        )
        return
      }
      const registration = await navigator.serviceWorker.ready
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          // Required by every browser that implements push: a notification the customer can see, for
          // every message. Silent push is not on offer and asking for it fails the subscribe.
          userVisibleOnly: true,
          // `.buffer`, because the DOM types accept a `BufferSource` and a `Uint8Array` over a
          // `SharedArrayBuffer` is not one. Legacy asserts the same way.
          applicationServerKey: vapidKeyBytes(publicKey).buffer as ArrayBuffer,
        }))
      const json = subscription.toJSON()
      await save.mutateAsync({ data: { endpoint: json.endpoint, keys: json.keys } as never })
      setHasSubscription(true)
    } catch {
      setProblem(tt('Predplatné sa nepodarilo uložiť. Skúste to znova.'))
    } finally {
      setBusy(false)
    }
  }, [publicKey, save])

  /**
   * Two facts, so two writes — legacy's order.
   *
   * The server row is what a campaign reads and the browser subscription is what a push arrives on;
   * dropping one and leaving the other is how a customer keeps receiving notifications they switched
   * off, or how the server keeps pushing at an endpoint nobody listens to. The DELETE goes first so a
   * failure leaves the browser subscribed and the row intact — a state the next attempt can repeat.
   */
  const disable = useCallback(async () => {
    setProblem('')
    if (!supported) return
    setBusy(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription !== null) {
        await drop.mutateAsync({ data: { endpoint: subscription.endpoint } })
        await subscription.unsubscribe()
      }
      setHasSubscription(false)
    } catch {
      setProblem(tt('Upozornenia sa nepodarilo vypnúť. Skúste to znova.'))
    } finally {
      setBusy(false)
    }
  }, [drop, supported])

  return { state, publicKey, busy, problem, enable, disable }
}
